import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../../web/components/Select';
import {
	ManagerCombobox,
	ManagerComboboxStatus,
	UserComboboxAvatarItem,
} from '../../../../../../web/components/UserCombobox';
import { Button } from '../../../../../../web/components-v2/Button';
import {
	ComboboxClear,
	ComboboxContent,
	ComboboxControl,
	ComboboxInput,
	ComboboxList,
	ComboboxValue,
} from '../../../../../../web/components-v2/Combobox';
import { bs } from '../../../../../../web/styles/styles';
import { CompanyTagsSelect } from '../../../../../components/leadView/components/CompanyTagsSelect';
import { OutcomeMultiSelectDropdown } from '../../../../../components/leadView/components/OutcomeMultiSelectDropdown';
import { TeamMultiSelect } from '../../../../../components/leadView/components/TeamMultiSelect';
import { UserMultiSelect } from '../../../../../components/leadView/components/UserMultiSelect';
import type { ILeadViewFilter } from '../../../../../entities/LeadView/useLeadView';
import { styleSheet } from './styles';

interface CallFiltersProps {
	filter: ILeadViewFilter;
	onFilterChange: (filter: ILeadViewFilter) => void;
	onClearFilters: () => void;
}

const dateTypeOptions = [
	{ label: 'Call Date', value: 'call-date' },
	{ label: 'Demo Date', value: 'demo-date' },
];

const recordingChannelsOptions = [
	{ label: 'All Channels', value: 'all' },
	{ label: 'Rep Only', value: '1' },
	{ label: 'All Parties', value: '2' },
];

export const CallFilters: React.FC<CallFiltersProps> = ({ filter, onFilterChange, onClearFilters }) => {
	const id = React.useId();
	// Generic handler for filter changes
	const handleFilterChange = React.useCallback(
		(key: keyof ILeadViewFilter) => {
			return (item: any) => {
				if (!item) {
					const newFilter = { ...filter };
					delete newFilter[key];
					onFilterChange(newFilter);
					return;
				}
				onFilterChange({ ...filter, [key]: item });
			};
		},
		[filter, onFilterChange]
	);

	// Special handler for team multi-select
	const handleTeamFilterChange = React.useCallback(
		(options: any[]) => {
			if (!options) {
				const newFilter = { ...filter };
				delete newFilter.teams;
				onFilterChange(newFilter);
				return;
			}

			// Extract team names from checked options (excluding "All SDRs" helper)
			const selectedTeams = options
				.filter(option => option.checked && option.id !== 'all-sdrs')
				.map(option => option.dataContext || option.name);

			onFilterChange({ ...filter, teams: selectedTeams });
		},
		[filter, onFilterChange]
	);

	// Special handler for user multi-select with backward compatibility
	const handleUserFilterChange = React.useCallback(
		(users: IUser[]) => {
			if (!users || users.length === 0) {
				const newFilter = { ...filter };
				delete newFilter.user;
				delete newFilter.users;
				onFilterChange(newFilter);
				return;
			}
			// Set both user (for backward compatibility) and users (for multi-select)
			// user field gets the first selected user for backward compatibility
			onFilterChange({
				...filter,
				user: users[0], // First user for backward compatibility
				users, // All users for multi-select
			});
		},
		[filter, onFilterChange]
	);

	// Date range presets
	const dateRangePresets = [
		{ label: 'Today', value: 'today' },
		{ label: 'Yesterday', value: 'yesterday' },
		{ label: 'Last 7 Days', value: 'last7days' },
		{ label: 'Last 30 Days', value: 'last30days' },
		{ label: 'This Month', value: 'thismonth' },
		{ label: 'Last Month', value: 'lastmonth' },
		{ label: 'Custom', value: 'custom' },
	];

	const handleDateRangePresetChange = React.useCallback(
		(preset: string) => {
			if (preset === 'custom') return; // Don't change dates for custom option

			const now = new Date();
			const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
			let startDate: Date;
			let endDate: Date = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1); // End of today

			switch (preset) {
				case 'today':
					startDate = todayDate;
					break;
				case 'yesterday':
					startDate = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() - 1);
					break;
				case 'last7days':
					startDate = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'last30days':
					startDate = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
					endDate = new Date(todayDate.getTime() + 24 * 60 * 60 * 1000 - 1);
					break;
				case 'thismonth':
					startDate = new Date(now.getFullYear(), now.getMonth(), 1);
					endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
					break;
				case 'lastmonth':
					startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
					endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
					break;
				default:
					return;
			}

			onFilterChange({
				...filter,
				date: { start: startDate, end: endDate },
			});
		},
		[filter, onFilterChange]
	);

	const handleDateRangeChange = React.useCallback(
		(field: 'start' | 'end', date: string) => {
			const currentDate = filter.date || { start: new Date(), end: new Date() };
			const newDate = {
				...currentDate,
				[field]: new Date(date),
			};
			onFilterChange({
				...filter,
				date: newDate,
			});
		},
		[filter, onFilterChange]
	);

	// Determine current preset selection based on the date range
	const getCurrentPreset = React.useCallback(() => {
		if (!filter.date) return 'today';

		const { start, end } = filter.date;
		const now = new Date();
		const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
		const yesterday = new Date(todayDate.getTime() - 24 * 60 * 60 * 1000);

		// Check if it matches today
		if (start.getTime() >= todayDate.getTime() && end.getTime() <= todayDate.getTime() + 24 * 60 * 60 * 1000) {
			return 'today';
		}

		// Check if it matches yesterday
		if (start.getTime() >= yesterday.getTime() && end.getTime() <= todayDate.getTime()) {
			return 'yesterday';
		}

		// Check if it matches last 7 days (approximately)
		const sevenDaysAgo = new Date(todayDate.getTime() - 7 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - sevenDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last7days';
		}

		// Check if it matches last 30 days (approximately)
		const thirtyDaysAgo = new Date(todayDate.getTime() - 30 * 24 * 60 * 60 * 1000);
		if (
			Math.abs(start.getTime() - thirtyDaysAgo.getTime()) < 24 * 60 * 60 * 1000 &&
			end.getTime() > todayDate.getTime()
		) {
			return 'last30days';
		}

		// Default to custom
		return 'custom';
	}, [filter.date]);

	const today = new Date().toISOString().split('T')[0];

	return (
		<div className={css(bs.flex, bs.flexCol, bs.gap2)}>
			<div className={css(styleSheet.clearFilterGroup)}>
				<Button variant='secondary' size='sm' onClick={onClearFilters}>
					✕ Clear All Filters
				</Button>
			</div>
			<div className={`${css(styleSheet.container)} callFilters`}>
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)} htmlFor='date-type'>
						Date Type
					</label>
					<Select
						items={dateTypeOptions}
						value={filter.dateType || 'call-date'}
						onValueChange={handleFilterChange('dateType')}
					>
						<SelectTrigger id='date-type' styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{dateTypeOptions.map(option => (
								<SelectItem key={option.value} value={option.value}>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Date Range Helper */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)} htmlFor='quick-select'>
						Quick Select
					</label>
					<Select items={dateRangePresets} value={getCurrentPreset()} onValueChange={handleDateRangePresetChange}>
						<SelectTrigger id='quick-select' styles={[bs.wFull]}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{dateRangePresets.map(preset => (
								<SelectItem key={preset.value} value={preset.value}>
									{preset.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				{/* Date Range */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Date Range</label>
					<div className={css(styleSheet.dateRange)}>
						<input
							type='date'
							value={filter.date?.start?.toISOString().split('T')[0] || today}
							onChange={e => handleDateRangeChange('start', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
						<span className={css(styleSheet.dateSeparator)}>to</span>
						<input
							type='date'
							value={filter.date?.end?.toISOString().split('T')[0] || today}
							onChange={e => handleDateRangeChange('end', e.target.value)}
							className={css(styleSheet.dateInput)}
						/>
					</div>
				</div>

				{/* Recording Channels Filter */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Recording Channels</label>
					<div className={css(styleSheet.selectWrapper)}>
						<Select
							items={recordingChannelsOptions}
							value={filter.recordingChannels || 'all'}
							onValueChange={value => {
								if (value === 'all') {
									handleFilterChange('recordingChannels')(null);
									return;
								}
								handleFilterChange('recordingChannels')(value);
							}}
						>
							<SelectTrigger styles={[bs.wFull]}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{recordingChannelsOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
				</div>

				<UserMultiSelect users={filter.users || (filter.user ? [filter.user] : [])} onChange={handleUserFilterChange} />

				<ManagerCombobox id={filter.manager?.id} key={filter.manager?.id} onValueChange={handleFilterChange('manager')}>
					<div className={css(bs.flex, bs.flexCol, bs.gap1)}>
						<label htmlFor={`${id}-manager-combobox`} className={css(bs.textXs, bs.fontBold)}>
							Manager
						</label>
						<ComboboxControl>
							<ComboboxValue>
								<ComboboxInput placeholder='Filter by manager' id={`${id}-manager-combobox`} />
							</ComboboxValue>
							<ComboboxClear />
						</ComboboxControl>
					</div>
					<ComboboxContent>
						<ManagerComboboxStatus />
						<ComboboxList>{(user: IUser) => <UserComboboxAvatarItem key={user.id} user={user} />}</ComboboxList>
					</ComboboxContent>
				</ManagerCombobox>

				{/* Outcome Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Outcomes</label>
					<div className={css(styleSheet.selectWrapper)}>
						<OutcomeMultiSelectDropdown selectedOutcomes={filter.outcomes} onChange={handleFilterChange('outcomes')} />
					</div>
				</div>

				{/* Team Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Teams</label>
					<div className={css(styleSheet.selectWrapper)}>
						<TeamMultiSelect onChange={handleTeamFilterChange} selectedTeams={filter.teams || []} />
					</div>
				</div>

				{/* Company Tags Selector */}
				<div className={css(styleSheet.filterGroup)}>
					<label className={css(styleSheet.label)}>Company Tags</label>
					<div className={css(styleSheet.selectWrapper)}>
						<CompanyTagsSelect
							onChange={handleFilterChange('companyTag')}
							className={css(styleSheet.managerSelect)}
							autoCompleteInputWrapperStyles={[styleSheet.managerSelectAutoCompleteInputWrapper]}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};
